<template>
  <v-container>
    <v-layout row>
      <v-flex xs12 sm8 offset-sm2>
        <div class="guest-form iabf-dancer-theme">

          <div class="form-header">
            <router-link to="/" class="back-link">← Back to Dashboard</router-link>
            <h1>{{ isFaculty ? 'Your guest' : 'Guest Registration' }}</h1>
            <p v-if="isFaculty" class="field-desc faculty-guest-lede">
              One guest is included with you for festival meals. Showcase seats are always free.
            </p>
          </div>

          <!-- Registered guests summary -->
          <div v-if="existingGuests.length > 0" class="guest-summary-section">
            <h2>Your Registered Guests</h2>
            <div class="guest-card" v-for="g in existingGuests" :key="g.id">
              <div class="guest-card-header">
                <span class="guest-card-name">{{ g.guestName }}</span>
                <span class="guest-card-status" :class="'status-' + effectiveStatus(g)">{{ formatPaymentStatus(effectiveStatus(g)) }}</span>
              </div>
              <div class="guest-card-details">
                <span v-if="g.attendingShow">Attending showcase</span>
                <span v-if="g.awardsAfterparty">Awards banquet</span>

                <span v-if="g.allMeals">All meals</span>
                <span v-else-if="g.meals && g.meals.length > 0">{{ g.meals.length }} meal{{ g.meals.length > 1 ? 's' : '' }}</span>
              </div>
              <div class="guest-card-total">${{ g.totalCost }}</div>
            </div>
            <p class="registration-note">
              To make changes to a registration, email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.<template v-if="guestRefundLabel"> No refunds after {{ guestRefundLabel }}.</template>
            </p>
            <div class="register-another-heading">
              <h2>Register Another Guest</h2>
            </div>
          </div>

          <!-- Past-deadline notice -->
          <div v-if="isPastDeadline" class="iabf-banner deadline-notice">
            <strong>Banquet &amp; meal registration is now closed.</strong>
            If your guest wants to join for meals or the awards banquet, contact Julie at <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>. Registration is closed, but we will do our best to fit you in.
          </div>

          <!-- Step 1: Guest name + show attendance -->
          <div v-if="step === 1">
            <h2>About Your Guest</h2>
            <p>We are so excited to welcome your guests to IABF!</p>
            <div class="field-group">
              <label class="field-label">Guest's full name *</label>
              <input v-model="guestName" class="text-input" type="text" placeholder="Full name" />
            </div>
            <div class="field-group">
              <label class="field-label">Is your guest coming to the showcase?</label>
              <p class="field-desc">The showcase is free for all guests — no ticket needed.</p>
              <div class="pill-group" role="group" aria-label="Coming to the showcase">
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': attendingShow === true }"
                  @click="attendingShow = true"
                >
                  Yes
                </button>
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': attendingShow === false }"
                  @click="attendingShow = false"
                >
                  No
                </button>
              </div>
            </div>
            <v-btn class="primary mt-4" :disabled="!guestName || attendingShow === null" @click="goFromStep1">Next</v-btn>
          </div>

          <!-- Step 2: Awards Afterparty (only if attending show) -->
          <div v-if="step === 2">
            <h2>Awards Dinner &amp; Afterparty</h2>
            <p>This promises to be a memorable evening, and we would love to have your guest join us after the showcase.</p>
            <div class="field-group">
              <label class="field-label">Would your guest like to join us for the Awards Dinner &amp; Afterparty?</label>
              <div class="pill-group" role="group" aria-label="Awards Dinner and Afterparty">
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': awardsAfterparty === true }"
                  @click="awardsAfterparty = true"
                >
                  Yes
                </button>
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': awardsAfterparty === false }"
                  @click="awardsAfterparty = false"
                >
                  No
                </button>
              </div>
            </div>
            <div v-if="awardsAfterparty" class="field-group">
              <div v-if="hasAwardsBanquetGift" class="gift-banner">
                🎁 Your guest's Awards Banquet dinner is complimentary — thank you for returning to IABF!
              </div>
              <label class="field-label">Choose an option</label>
              <div class="pill-group pill-group--stack" role="group" aria-label="Afterparty option">
                <button
                  v-for="opt in afterpartyOptions"
                  :key="opt.key"
                  type="button"
                  class="iabf-chip iabf-chip--block"
                  :class="{ 'is-selected': afterpartyOption === opt.key }"
                  @click="afterpartyOption = opt.key"
                >
                  <span v-if="hasAwardsBanquetGift && opt.key === 'dinner_only'">
                    {{ opt.label }} — <s class="gift-strikethrough">${{ opt.price }}</s> Free — thank you gift
                  </span>
                  <span v-else>{{ opt.label }} — ${{ opt.price }}</span>
                </button>
              </div>
            </div>
            <div class="step-nav">
              <v-btn @click="step = 1">Back</v-btn>
              <v-btn
                class="primary ml-3"
                :disabled="awardsAfterparty === null || (awardsAfterparty && !afterpartyOption)"
                @click="goFromAfterparty"
              >Next</v-btn>
            </div>
          </div>

          <!-- Step 4: Additional meals (step 3 lodging removed) -->
          <div v-if="step === 4">
            <h2>Additional Meals</h2>
            <p>Would your guest like to join us for any meals? All prices are per person.</p>
            <div class="field-group">
              <label class="checkbox-opt all-meals-opt">
                <input type="checkbox" v-model="allMeals" @change="onAllMealsChange" />
                <span v-if="hasWelcomeDinnerGift || hasAwardsBanquetGift">
                  <strong>All Meals — <s class="gift-strikethrough">$500</s> ${{ 500 - (hasWelcomeDinnerGift ? 100 : 0) - (hasAwardsBanquetGift ? 100 : 0) }}</strong> (best value — thank you gift applied)
                </span>
                <span v-else><strong>All Meals — $500</strong> (best value)</span>
              </label>
            </div>
            <div v-if="hasWelcomeDinnerGift || hasAwardsBanquetGift" class="gift-banner">
              🎁
              <span v-if="hasWelcomeDinnerGift && hasAwardsBanquetGift">Your guest's Welcome Dinner and Awards Banquet dinner are complimentary — thank you for returning to IABF!</span>
              <span v-else-if="hasWelcomeDinnerGift">Your guest's Welcome Dinner is complimentary — thank you for returning to IABF!</span>
              <span v-else>Your guest's Awards Banquet dinner is complimentary — thank you for returning to IABF!</span>
            </div>
            <div v-if="!allMeals" class="meal-grid">
              <label class="checkbox-opt" v-for="meal in mealOptions" :key="meal.key">
                <input type="checkbox" :value="meal.key" v-model="meals" @change="onBanquetCheck(meal.key)" />
                <span v-if="isMealGifted(meal.key)">
                  {{ meal.label }} — <s class="gift-strikethrough">${{ meal.price }}</s> Free — thank you gift
                </span>
                <span v-else>{{ meal.label }} — ${{ meal.price }}</span>
              </label>
            </div>
            <div class="step-nav">
              <v-btn @click="step = (attendingShow && !isPastDeadline) ? 2 : 1">Back</v-btn>
              <v-btn class="primary ml-3" @click="step = 5">Next</v-btn>
            </div>
          </div>

          <!-- Step 5: Dietary (same fields as dancer dietary form) -->
          <div v-if="step === 5">
            <h2>How does your guest like to eat?</h2>
            <p class="step-lede">
              Same questions we ask dancers — so the kitchen sees one consistent list.
            </p>
            <dietary-form-fields v-model="dietaryForm" subject="guest" />
            <p v-if="dietaryStepError" class="error-text" role="alert">{{ dietaryStepError }}</p>
            <div class="field-group">
              <label class="field-label">Any other questions or accommodations for your guest?</label>
              <textarea v-model="notes" class="text-area" placeholder="Optional..." rows="3"></textarea>
            </div>
            <div class="step-nav">
              <v-btn @click="step = isPastDeadline ? (attendingShow ? 2 : 1) : 4">Back</v-btn>
              <v-btn class="primary ml-3" @click="goFromDietary">Next</v-btn>
            </div>
          </div>

          <!-- Step 6: Summary + payment -->
          <div v-if="step === 6">
            <h2>Review &amp; Submit</h2>
            <div class="summary-card">
              <div class="summary-row"><span>Guest</span><strong>{{ guestName }}</strong></div>
              <div class="summary-row"><span>Attending show</span><strong>{{ attendingShow ? 'Yes' : 'No' }}</strong></div>
              <div v-if="attendingShow" class="summary-row">
                <span>Awards Afterparty</span>
                <strong>{{ awardsAfterparty ? selectedAfterpartyLabel : 'No' }}</strong>
              </div>
              <div class="summary-row">
                <span>Meals</span>
                <strong>{{ mealSummaryLabel }}</strong>
              </div>
              <div class="summary-row"><span>Dietary</span><strong>{{ dietarySummaryLine }}</strong></div>
              <div v-if="allergiesSummaryLine" class="summary-row"><span>Allergies</span><strong>{{ allergiesSummaryLine }}</strong></div>
              <div v-if="glutenSummaryLine" class="summary-row"><span>Gluten</span><strong>{{ glutenSummaryLine }}</strong></div>
              <div v-if="dietaryForm.dietaryNotes" class="summary-row"><span>Dietary notes</span><strong>{{ dietaryForm.dietaryNotes }}</strong></div>
              <div v-if="notes" class="summary-row"><span>Guest notes</span><strong>{{ notes }}</strong></div>
              <div class="summary-total">
                <span>Total</span>
                <strong>${{ calculatedTotal }}</strong>
              </div>
            </div>

            <div v-if="submitError" class="error-text mt-3">{{ submitError }}</div>

            <div v-if="calculatedTotal > 0" class="payment-options">
              <p class="convenience-note">Feel free to pay at your convenience — if this hasn't been paid before your final scheduled payment, it will be included in that charge.</p>
              <p class="field-label">How would you like to handle payment?</p>
              <v-btn class="primary mr-3" :loading="submitting === 'pay'" @click="submitAndPay">
                Submit &amp; Pay Now (${{ calculatedTotal }})
              </v-btn>
              <v-btn :loading="submitting === 'next'" @click="submitAndAddToNext">
                Submit &amp; Add to Next IABF Payment
              </v-btn>
            </div>
            <div v-else>
              <v-btn class="primary" :loading="submitting === 'free'" @click="submitFree">Submit</v-btn>
            </div>

            <div class="step-nav mt-2">
              <v-btn text @click="step = 5">Back</v-btn>
            </div>
          </div>

          <!-- Step 7: Confirmation -->
          <div v-if="step === 7" class="confirmation">
            <div class="confirm-icon">✓</div>
            <h2>{{ paymentMethod === 'added_to_next' ? 'Registered!' : paymentMethod === 'pay' ? 'Redirecting to payment…' : 'Registered!' }}</h2>
            <p v-if="paymentMethod === 'added_to_next'">
              {{ guestName }}'s registration has been submitted. ${{ calculatedTotal }} has been added to your next payment.
            </p>
            <p v-else-if="calculatedTotal === 0">
              {{ guestName }}'s registration has been submitted. No payment is due.
            </p>
            <v-btn class="primary mt-4" @click="registerAnother">Register Another Guest</v-btn>
            <router-link to="/" class="dashboard-link">Back to Dashboard</router-link>
          </div>

        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import DietaryFormFields from '@/components/iabf/DietaryFormFields.vue'
import {
  allergiesDisplayLine,
  dietaryDisplayLine,
  dietaryPayloadFromForm,
  emptyDietaryFormState,
  glutenDisplayLine,
  validateDietaryForm
} from '@/config/iabf-dietary'
import { isGuestRegistrationClosed, REGISTRATION_YEAR } from '@/config/iabf-registration-2027'
import { effectiveDeadlineIso, formatDeadlineDisplay } from '@/config/iabf-deadlines'
import {
  dancerHasAnyGift,
  WELCOME_DINNER_GIFT_LABELS,
  AWARDS_BANQUET_GIFT_LABELS
} from '@/config/iabf-gifts'

const MEAL_OPTIONS = [
  { key: 'welcome_dinner', label: 'Welcome Dinner + Cocktail Party', price: 100 },
  { key: 'tue_breakfast', label: 'Tuesday Breakfast', price: 20 },
  { key: 'tue_lunch', label: 'Tuesday Lunch', price: 30 },
  { key: 'tue_dinner', label: 'Tuesday Dinner', price: 40 },
  { key: 'wed_breakfast', label: 'Wednesday Breakfast', price: 20 },
  { key: 'wed_lunch', label: 'Wednesday Lunch', price: 30 },
  { key: 'wed_dinner', label: 'Wednesday Dinner', price: 40 },
  { key: 'thu_breakfast', label: 'Thursday Breakfast', price: 20 },
  { key: 'thu_lunch', label: 'Thursday Lunch', price: 30 },
  { key: 'thu_dinner', label: 'Thursday Dinner', price: 40 },
  { key: 'fri_breakfast', label: 'Friday Breakfast', price: 20 },
  { key: 'fri_lunch', label: 'Friday Lunch', price: 30 },
  // Transportation included with banquet in 2027+ — no separate transport option
  { key: 'fri_banquet', label: 'Friday Awards Banquet — Dinner', price: 100 },
  { key: 'sat_breakfast', label: 'Saturday Breakfast', price: 20 }
]

const BANQUET_KEYS = ['fri_banquet', 'fri_banquet_transport']

const AFTERPARTY_TO_MEAL = {
  dinner_only: 'fri_banquet'
}

const AFTERPARTY_OPTIONS = [
  { key: 'dinner_only', label: 'Afterparty & Dinner', price: 100 },
  { key: 'kids_menu', label: 'Kids Menu (under 13)', price: 25 }
]

export default {
  components: {
    DietaryFormFields
  },
  data () {
    return {
      step: 1,
      // Form fields
      guestName: '',
      attendingShow: null,
      awardsAfterparty: null,
      afterpartyOption: '',
      lodging: '',
      allMeals: false,
      meals: [],
      dietaryForm: emptyDietaryFormState(),
      dietaryStepError: '',
      notes: '',
      // State
      submitting: null,
      submitError: '',
      paymentMethod: '',
      addedToPaymentNum: null,
      savedGuestId: null,
      mealOptions: MEAL_OPTIONS,
      afterpartyOptions: AFTERPARTY_OPTIONS
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.initDancer()
  },
  computed: {
    user () {
      return this.$store.getters.user
    },
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    isFaculty () {
      return !!(this.dancer.isFaculty || this.dancer.personType === 'faculty')
    },
    facultyComplimentaryAvailable () {
      if (!this.isFaculty) {
        return false
      }
      return !this.existingGuests.some(g => g.facultyComplimentary === true)
    },
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) ||
        Number(this.dancer.festivalYear) ||
        REGISTRATION_YEAR
    },
    guestRefundLabel () {
      const iso = effectiveDeadlineIso('guestRefund', this.festivalYear)
      if (!iso) {
        return ''
      }
      return formatDeadlineDisplay(iso)
    },
    existingGuests () {
      return this.$store.getters.guestRegistrations
    },
    isPastDeadline () {
      return isGuestRegistrationClosed(this.festivalYear)
    },
    hasWelcomeDinnerGift () {
      const d = this.dancer
      if (!dancerHasAnyGift(d, ...WELCOME_DINNER_GIFT_LABELS)) {
        return false
      }
      return !this.existingGuests.some(g => g.usedWelcomeDinnerGift === true)
    },
    dietarySummaryLine () {
      return dietaryDisplayLine(this.dietaryForm) || '—'
    },
    allergiesSummaryLine () {
      return allergiesDisplayLine(this.dietaryForm)
    },
    glutenSummaryLine () {
      return glutenDisplayLine(this.dietaryForm)
    },
    hasAwardsBanquetGift () {
      const d = this.dancer
      if (!dancerHasAnyGift(d, ...AWARDS_BANQUET_GIFT_LABELS)) {
        return false
      }
      return !this.existingGuests.some(g => g.usedBanquetGift === true)
    },
    calculatedTotal () {
      if (this.facultyComplimentaryAvailable) {
        return 0
      }
      let total = 0
      // Kids menu ($25) is not in MEAL_OPTIONS so count it separately from afterpartyOption
      // dinner_only cost is captured via fri_banquet in meals
      if (this.awardsAfterparty && this.afterpartyOption === 'kids_menu') {
        total += 25
      }
      if (this.allMeals) {
        let allMealsPrice = 500
        if (this.hasWelcomeDinnerGift) allMealsPrice -= 100
        if (this.hasAwardsBanquetGift) allMealsPrice -= 100
        total += allMealsPrice
      } else {
        this.meals.forEach(key => {
          const meal = MEAL_OPTIONS.find(m => m.key === key)
          if (!meal) return
          if (this.isMealGifted(key)) return
          total += meal.price
        })
      }
      return total
    },
    selectedAfterpartyLabel () {
      const opt = AFTERPARTY_OPTIONS.find(o => o.key === this.afterpartyOption)
      return opt ? opt.label + ' ($' + opt.price + ')' : ''
    },
    mealSummaryLabel () {
      if (this.allMeals) return 'All Meals ($500)'
      if (this.meals.length === 0) return 'None'
      return this.meals.map(k => {
        const m = MEAL_OPTIONS.find(o => o.key === k)
        return m ? m.label : k
      }).join(', ')
    }
  },
  methods: {
    isMealGifted (key) {
      if (key === 'welcome_dinner' && this.hasWelcomeDinnerGift) return true
      if (key === 'fri_banquet' && this.hasAwardsBanquetGift) return true
      return false
    },
    effectiveStatus (g) {
      if (g.paymentStatus === 'added_to_next' && g.addedToPaymentNum) {
        const paymentField = `payment${g.addedToPaymentNum}Status`
        if (this.dancer[paymentField] === 'Paid') return 'paid'
      }
      return g.paymentStatus
    },
    formatPaymentStatus (status) {
      return { paid: 'Paid', unpaid: 'Unpaid', added_to_next: 'Added to next payment' }[status] || status
    },
    initDancer () {
      const dancer = this.$store.getters.currentIABFDancer
      if (dancer && dancer.id) {
        this.$store.dispatch('loadGuestRegistrationsForDancer', (dancer.email || '').toLowerCase())
        return
      }
      if (!this.user) return
      const uid = this.user.id  // store uses .id, not .uid
      const email = this.user.email
      this.$store.dispatch('loadIABFDancerByUid', uid).then(() => {
        const loaded = this.$store.getters.currentIABFDancer
        if (loaded && loaded.id) {
          this.$store.dispatch('loadGuestRegistrationsForDancer', (loaded.email || '').toLowerCase())
        } else if (email) {
          // Fallback: find by email (also links UID to the dancer doc)
          this.$store.dispatch('linkIABFDancerByEmail', { email, uid }).then(() => {
            const linked = this.$store.getters.currentIABFDancer
            if (linked && linked.id) {
              this.$store.dispatch('loadGuestRegistrationsForDancer', (linked.email || '').toLowerCase())
            }
          })
        }
      })
    },
    onAllMealsChange () {
      if (this.allMeals) {
        this.meals = []
      } else {
        // Restore banquet preference from afterparty selection
        if (this.awardsAfterparty && this.afterpartyOption && AFTERPARTY_TO_MEAL[this.afterpartyOption]) {
          this.meals = [AFTERPARTY_TO_MEAL[this.afterpartyOption]]
        }
      }
    },
    goFromStep1 () {
      // Skip lodging (removed). Afterparty only if attending and meals still open.
      if (this.attendingShow && !this.isPastDeadline) {
        this.step = 2
        return
      }
      if (this.isPastDeadline) {
        this.step = 5
        return
      }
      this.enterMealsStep()
    },
    goFromAfterparty () {
      if (this.isPastDeadline) {
        this.step = 5
        return
      }
      this.enterMealsStep()
    },
    enterMealsStep () {
      // Pre-populate banquet from afterparty selection
      if (this.awardsAfterparty && this.afterpartyOption && AFTERPARTY_TO_MEAL[this.afterpartyOption]) {
        const mealKey = AFTERPARTY_TO_MEAL[this.afterpartyOption]
        this.meals = this.meals.filter(k => !BANQUET_KEYS.includes(k))
        this.meals.push(mealKey)
      }
      this.step = 4
    },
    onBanquetCheck (key) {
      if (!BANQUET_KEYS.includes(key)) return
      if (this.meals.includes(key)) {
        // Just checked this banquet key — remove the other
        BANQUET_KEYS.forEach(bk => {
          if (bk !== key) {
            const idx = this.meals.indexOf(bk)
            if (idx !== -1) this.meals.splice(idx, 1)
          }
        })
      }
    },
    goFromDietary () {
      this.dietaryStepError = ''
      const err = validateDietaryForm(this.dietaryForm)
      if (err) {
        this.dietaryStepError = err
        return
      }
      this.step = 6
    },
    buildPayload () {
      const dietary = dietaryPayloadFromForm(this.dietaryForm)
      return {
        dancerId: this.dancer.id,
        dancerName: this.dancer.firstName + ' ' + this.dancer.lastName,
        dancerEmail: (this.dancer.email || '').toLowerCase(),
        guestName: this.guestName,
        attendingShow: this.attendingShow,
        lodging: null,
        awardsAfterparty: this.awardsAfterparty || false,
        afterpartyOption: this.awardsAfterparty ? this.afterpartyOption : null,
        allMeals: this.allMeals,
        meals: this.allMeals ? [] : this.meals,
        // Same shape as dancer dietaryRestrictions (for meal exports)
        diet: dietary.diet,
        dietDetails: dietary.dietDetails,
        gluten: dietary.gluten,
        foodsNotConsumed: dietary.foodsNotConsumed,
        foodAllergies: dietary.foodAllergies,
        dietaryNotes: dietary.dietaryNotes,
        // Legacy guest fields kept in sync for older admin views
        dietaryRestriction: dietary.dietaryRestriction,
        otherAllergies: dietary.otherAllergies,
        notes: this.notes,
        totalCost: this.calculatedTotal,
        paymentStatus: 'unpaid',
        facultyComplimentary: this.facultyComplimentaryAvailable,
        // allMeals also consumes these gifts (price is discounted) — record it
        // so gift editing can't swap them away after the fact.
        usedWelcomeDinnerGift: this.hasWelcomeDinnerGift &&
          (this.allMeals || this.meals.includes('welcome_dinner')),
        usedBanquetGift: this.hasAwardsBanquetGift &&
          (this.allMeals || BANQUET_KEYS.some(k => this.meals.includes(k)))
      }
    },
    nextUnpaidPayment () {
      for (let i = 1; i <= 6; i++) {
        if (this.dancer[`payment${i}Status`] !== 'Paid') {
          return i
        }
      }
      return null
    },
    async submitAndPay () {
      this.submitting = 'pay'
      this.submitError = ''
      try {
        const guestId = await this.$store.dispatch('saveGuestRegistration', this.buildPayload())
        this.savedGuestId = guestId
        const baseUrl = window.location.origin
        const url = await this.$store.dispatch('createGuestCheckoutSession', {
          dancerId: this.dancer.id,
          guestIds: [guestId],
          lineItems: [{ description: 'IABF ' + this.festivalYear + ' Guest — ' + this.guestName, amount: this.calculatedTotal }],
          successUrl: baseUrl + '/iabf/guests?payment_success=true',
          cancelUrl: baseUrl + '/iabf/guests'
        })
        window.location.href = url
      } catch (e) {
        this.submitError = 'Something went wrong. Please try again.'
        this.submitting = null
      }
    },
    async submitAndAddToNext () {
      this.submitting = 'next'
      this.submitError = ''
      const paymentNum = this.nextUnpaidPayment()
      if (!paymentNum) {
        this.submitError = 'No upcoming payment found. Please contact hello@iabf.dance.'
        this.submitting = null
        return
      }
      try {
        const guestId = await this.$store.dispatch('saveGuestRegistration', this.buildPayload())
        const currentAmount = this.dancer[`payment${paymentNum}Due`] || 0
        const newAmount = currentAmount + this.calculatedTotal
        await this.$store.dispatch('assignCostumeColor', {
          id: this.dancer.id,
          field: `payment${paymentNum}Due`,
          color: newAmount
        })
        await this.$store.dispatch('setGuestsAddedToNext', { guestIds: [guestId], paymentNum })
        this.addedToPaymentNum = paymentNum
        this.paymentMethod = 'added_to_next'
        this.step = 7
      } catch (e) {
        console.error('submitAndAddToNext error:', e)
        this.submitError = 'Something went wrong. Please try again.'
      }
      this.submitting = null
    },
    async submitFree () {
      this.submitting = 'free'
      this.submitError = ''
      try {
        await this.$store.dispatch('saveGuestRegistration', {
          ...this.buildPayload(),
          paymentStatus: 'paid'
        })
        this.paymentMethod = 'free'
        this.step = 7
      } catch (e) {
        this.submitError = 'Something went wrong. Please try again.'
      }
      this.submitting = null
    },
    registerAnother () {
      this.step = 1
      this.guestName = ''
      this.attendingShow = null
      this.awardsAfterparty = null
      this.afterpartyOption = ''
      this.lodging = null
      this.allMeals = false
      this.meals = []
      this.dietaryForm = emptyDietaryFormState()
      this.dietaryStepError = ''
      this.notes = ''
      this.submitting = null
      this.submitError = ''
      this.paymentMethod = ''
      this.addedToPaymentNum = null
      this.savedGuestId = null
    }
  },
  watch: {
    user (val) {
      if (val) this.initDancer()
    },
    '$route.query.payment_success' (val) {
      if (val === 'true' && this.savedGuestId) {
        this.$store.dispatch('setGuestsPaid', [this.savedGuestId])
        this.paymentMethod = 'paid'
        this.step = 7
      }
    }
  }
}
</script>

<style scoped>
.guest-form {
  max-width: 680px;
  margin: 0 auto;
  padding: 40px 20px 80px;
  color: var(--iabf-ink, #1c1b1f);
}

.form-header {
  margin-bottom: 40px;
}

.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  text-decoration: none;
  display: block;
  margin-bottom: 16px;
}

h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  margin: 0 0 6px 0;
}

h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 26px;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}

p, label {
  font-family: var(--iabf-font-body, inherit);
  font-size: 17px;
  line-height: 1.7;
  color: var(--iabf-ink-muted, #5c5a63);
}

.subtitle {
  color: var(--iabf-ink-faint, #8a8792);
  font-size: 16px;
  margin: 0;
}

.step-lede {
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.45;
  margin: 0 0 18px;
}
.field-group {
  margin-bottom: 24px;
}

.field-label {
  display: block;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 16px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
  margin-bottom: 8px;
}

.field-desc {
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  margin: -4px 0 10px 0;
}

.text-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  box-sizing: border-box;
}

.text-area {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  box-sizing: border-box;
  resize: vertical;
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pill-group--stack {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
}
.iabf-chip--block {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  min-height: 48px;
  border-radius: 14px;
  line-height: 1.35;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.chip-sub {
  font-size: 13px;
  font-weight: 400;
  color: var(--iabf-ink-muted, #5c5a63);
}
.iabf-chip.is-selected .chip-sub {
  color: var(--iabf-ink, #1c1b1f);
}

.meal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: column;
  grid-template-rows: repeat(8, auto);
  gap: 10px 24px;
  margin-top: 12px;
}

.checkbox-opt {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 16px;
  cursor: pointer;
  color: var(--iabf-ink, #1c1b1f);
}

.checkbox-opt input {
  margin-top: 4px;
  flex-shrink: 0;
}

.all-meals-opt {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 12px 16px;
  margin-bottom: 12px;
}

.step-nav {
  margin-top: 24px;
  display: flex;
  align-items: center;
}

.summary-card {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 20px 24px;
  margin-bottom: 24px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
}

.summary-row span { color: var(--iabf-ink-faint, #8a8792); }

.summary-total {
  display: flex;
  justify-content: space-between;
  padding: 12px 0 0 0;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 20px;
  color: var(--iabf-ink, #1c1b1f);
  margin-top: 4px;
}

.payment-options {
  margin-top: 8px;
}

.convenience-note {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 16px;
  line-height: 1.6;
}

.deadline-notice {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 20px 22px;
  margin-bottom: 24px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  line-height: 1.5;
}

.deadline-notice a {
  color: var(--iabf-primary, #8a957d);
}

.registration-note {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 8px 0 0 0;
  line-height: 1.5;
}

.registration-note a {
  color: var(--iabf-ink-muted, #5c5a63);
}

.error-text {
  color: var(--iabf-danger, #b54a4a);
  font-family: var(--iabf-font-body, inherit);
}

.guest-summary-section {
  margin-bottom: 40px;
}

.guest-card {
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 16px 20px;
  margin-bottom: 12px;
  background: #ffffff;
}

.guest-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.guest-card-name {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 17px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}

.guest-card-status {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
}

.status-paid { background: #e8f5e9; color: var(--iabf-primary, #8a957d); }
.status-unpaid { background: #fff3e0; color: #e65100; }
.status-added_to_next { background: #e3f2fd; color: #1565c0; }

.guest-card-details {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 6px;
}

.guest-card-total {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  font-weight: 700;
  text-align: right;
}

.register-another-heading {
  margin-top: 32px;
}

.register-another-heading h2 {
  margin-bottom: 0;
}

.confirmation {
  text-align: center;
  padding: 40px 0;
}

.confirm-icon {
  font-size: 48px;
  color: var(--iabf-primary, #8a957d);
  margin-bottom: 16px;
}

.dashboard-link {
  display: block;
  margin-top: 20px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
  text-decoration: underline;
}

.gift-banner {
  background: #f0ebe4;
  border: 1px solid #d4c8b8;
  border-radius: 4px;
  padding: 12px 16px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: #5a3e2b;
  margin-bottom: 16px;
}

.gift-strikethrough {
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 400;
  margin-right: 6px;
}
</style>
